<template>
  <div class="h-screen bg-gray-50 flex flex-col">
    <!-- Top Header -->
    <AdminHeader 
      :current-project="currentProject"
      :current-page="currentPage"
      :has-unsaved-changes="hasUnsavedChanges"
      @save="handleSave"
      @publish="handlePublish"
      @preview="handlePreview"
      @edit-page="handleEditPage"
    />
    
    <!-- Main Content Area -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Left Sidebar: Page Tree -->
      <PageTreeSidebar
        v-if="currentProject && route.name === 'page-editor'"
        :project="currentProject"
        :pages="projectPages ?? []"
        :current-page="currentPage || undefined"
        @select-page="handleSelectPage"
        @create-page="handleCreatePage"
        @delete-page="handleDeletePage"
      />
      
      <!-- Main Content -->
      <main class="flex-1 flex overflow-y-auto">
        <RouterView />
      </main>
      
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AdminHeader from './AdminHeader.vue'
import PageTreeSidebar from './PageTreeSidebar.vue'
import { useProjectQuery, useProjectBySlugQuery } from '@/composables/useProjects'
import { usePagesQuery, usePageQuery, useUpdatePageMutation, useUpdateComponentMutation, usePageComponentsQuery, useCreatePageMutation, useDeletePageMutation, usePublishPageMutation } from '@/composables/usePages'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { usePreviewUrl } from '@/composables/usePreviewUrl'
import { useUIStore } from '@/stores/ui'
import { trackActivity } from '@/utils/activity'
import { useI18n } from 'vue-i18n'

const route = useRoute()
const router = useRouter()
const uiStore = useUIStore()
const confirm = useConfirm()
const toast = useToast()
const { t } = useI18n()
const { openPreview } = usePreviewUrl()

// Extract route parameters
const projectId = computed(() => parseInt(route.params.projectId as string) || 0)
const pageId = computed(() => parseInt(route.params.pageId as string) || 0)

// Use TanStack Query to fetch data
const { data: currentProject } = useProjectQuery(projectId)
const { data: currentPage } = usePageQuery(pageId)

// Create a computed for the project slug to ensure reactivity
const projectSlug = computed(() => currentProject.value?.slug || '')

// Pass the computed ref directly to make the query fully reactive
const { data: projectPages } = usePagesQuery(projectSlug)

// Page components query
const { data: components } = usePageComponentsQuery(pageId)

// Mutations
const updatePageMutation = useUpdatePageMutation()
const updateComponentMutation = useUpdateComponentMutation()
const createPageMutation = useCreatePageMutation()
const deletePageMutation = useDeletePageMutation()
const publishPageMutation = usePublishPageMutation()

// Use store for unsaved changes tracking
const hasUnsavedChanges = computed(() => uiStore.hasUnsavedChanges)

// Event handlers
const handleSave = async () => {
  const pendingChanges = uiStore.getPendingChanges()

  if (pendingChanges.size === 0) {
    toast.add({ severity: 'info', summary: 'No Changes', detail: 'Nothing to save.', life: 2000 })
    return
  }

  trackActivity('save', { pageId: pageId.value, componentCount: pendingChanges.size })

  let savedCount = 0
  let failedCount = 0

  for (const [componentId, changes] of pendingChanges) {
    const component = components.value?.find((c: any) => c.id === componentId)
    if (!component) {
      failedCount++
      continue
    }

    const updatedData = { ...component.contentData, ...changes }

    try {
      await updateComponentMutation.mutateAsync({
        id: componentId,
        pageId: pageId.value,
        contentData: updatedData
      })
      savedCount++
    } catch (error) {
      console.error('Failed to save component', componentId, error)
      failedCount++
    }
  }

  uiStore.clearPendingChanges()

  if (failedCount === 0) {
    toast.add({ severity: 'success', summary: t('admin.saveToast.summary'), detail: t('admin.saveToast.detail', savedCount, { named: { count: savedCount } }), life: 4000 })
  } else {
    toast.add({ severity: 'warn', summary: 'Partial Save', detail: `${savedCount} saved, ${failedCount} failed.`, life: 5000 })
  }
}

const handlePublish = async () => {
  if (!currentPage.value) return
  trackActivity('publish', { pageId: pageId.value, pageTitle: currentPage.value.title })

  try {
    // Auto-save pending changes before publishing
    if (uiStore.hasUnsavedChanges) {
      await handleSave()
    }

    await publishPageMutation.mutateAsync({
      pageId: currentPage.value.id
    })

    toast.add({
      severity: 'success',
      summary: 'Page Published',
      detail: `"${currentPage.value.title}" has been published successfully.`,
      life: 3000
    })
  } catch (error) {
    console.error('Failed to publish page:', error)
    toast.add({
      severity: 'error',
      summary: 'Publish Failed',
      detail: 'Failed to publish the page. Please try again.',
      life: 5000
    })
  }
}

const handlePreview = async () => {
  // Open preview in new tab with correct i18n locale prefix
  if (currentProject.value && currentPage.value) {
    await openPreview(currentProject.value.slug, currentPage.value.slug)
  }
}

const handleSelectPage = async (page: any) => {
  // Navigate to the selected page using router
  if (currentProject.value) {
    router.push(`/admin/project/${currentProject.value.id}/page/${page.id}`)
  }
}

const handleCreatePage = async (parentId?: number) => {
  if (!currentProject.value) return
  trackActivity('create-page', { projectId: projectId.value })

  // Generate a unique slug based on timestamp
  const timestamp = Date.now()
  const newPageData = {
    projectId: currentProject.value.id,
    title: 'New Page',
    slug: `new-page-${timestamp}`,
    layoutType: 'default' as const
  }

  try {
    const newPage = await createPageMutation.mutateAsync(newPageData)

    toast.add({
      severity: 'success',
      summary: 'Page Created',
      detail: 'New page has been created. You can rename it in page settings.',
      life: 3000
    })

    // Navigate to the new page
    router.push(`/admin/project/${currentProject.value.id}/page/${newPage.id}`)
  } catch (error) {
    console.error('Failed to create page:', error)
    toast.add({
      severity: 'error',
      summary: 'Create Failed',
      detail: 'Failed to create the page. Please try again.',
      life: 5000
    })
  }
}

const handleDeletePage = async (pageIdToDelete: number) => {
  // Find page name for confirmation message
  const pages = projectPages.value ?? []
  const pageToDelete = pages.find((p: any) => p.id === pageIdToDelete)
  const pageName = pageToDelete?.title || 'this page'
  trackActivity('delete-page', { pageId: pageIdToDelete, pageTitle: pageName })

  confirm.require({
    message: `Are you sure you want to delete "${pageName}"? This action cannot be undone.`,
    header: 'Delete Page',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await deletePageMutation.mutateAsync(pageIdToDelete)

        toast.add({
          severity: 'success',
          summary: 'Page Deleted',
          detail: `"${pageName}" has been deleted successfully.`,
          life: 3000
        })

        // If we deleted the current page, navigate to project dashboard or first available page
        if (pageId.value === pageIdToDelete && currentProject.value) {
          const remainingPages = pages.filter((p: any) => p.id !== pageIdToDelete)
          if (remainingPages && remainingPages.length > 0) {
            router.push(`/admin/project/${currentProject.value.id}/page/${remainingPages[0].id}`)
          } else {
            router.push(`/admin/project/${currentProject.value.id}`)
          }
        }
      } catch (error) {
        console.error('Failed to delete page:', error)
        toast.add({
          severity: 'error',
          summary: 'Delete Failed',
          detail: 'Failed to delete the page. Please try again.',
          life: 5000
        })
      }
    }
  })
}

const handleEditPage = async (pageData: any) => {
  try {
    await updatePageMutation.mutateAsync(pageData)
    toast.add({
      severity: 'success',
      summary: 'Page Updated',
      detail: 'Page settings have been saved successfully.',
      life: 3000
    })
  } catch (error) {
    console.error('Failed to update page:', error)
    toast.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: 'Failed to update page settings. Please try again.',
      life: 5000
    })
  }
}
</script>